import { Button } from '@assembly-js/design-system';
import { CognitoHostedUIIdentityProvider } from '@aws-amplify/auth';
import { CognitoUser } from 'amazon-cognito-identity-js';
import { Auth } from 'aws-amplify';
import { AxiosResponse } from 'axios';
import { useFormikContext } from 'formik';
import { filter } from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';

import OnboardingClient from 'src/clients/OnboardingClient';
import UsersClient from 'src/clients/UsersClient';
import { FOUND_PORTAL_OTHER_VALUE } from 'src/constants';
import { PortalConfigContext } from 'src/context';
import { PortalConfig } from 'src/entities/PortalConfig';
import { useAppDispatch } from 'src/hooks/useStore';
import {
  CustomizationOptions,
  getPriorityLabels,
} from 'src/routes/onboarding/setup/components/customize-your-workspace/utils';
import {
  OnboardingContentLayout,
  OnboardingQuestionLabel,
} from 'src/routes/onboarding/setup/components/OnboardingContentLayout';
import { ToggleChipGroup } from 'src/routes/onboarding/setup/components/ToggleChipGroup';
import { useSetFormPageInfo } from 'src/routes/onboarding/setup/context/formPageInfoContext';
import { OnboardingFormValues } from 'src/routes/onboarding/setup/types';
import { alertSnackbar } from 'src/store/ui/actions';
import { TrackEvent, TrackPartnerStackSignUp } from 'src/utils/AnalyticsUtils';
import { getAuthRequestInfo, GetSignInMetadata } from 'src/utils/AuthUtils';
import { CognitoUserWithAttributes } from 'src/utils/CognitoUtils';
import { ensureApiError } from 'src/utils/Errors';

const validationSchema = toFormikValidationSchema(
  z.object({
    enabledFeatures: z.array(z.string()).min(1),
  }),
);

const trackInfoSubmitted = (values: OnboardingFormValues) => {
  TrackEvent('Onboarding - Customize Your Workspace', {
    industry: values.industry,
    priorities: values.priorities,
    enabledFeatures: values.enabledFeatures,
  });
};

export const CustomizeYourWorkspace = ({
  priorityOptions,
  featureOptions,
}: CustomizationOptions) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const portalConfig = useContext(PortalConfigContext);
  const [skipped, setSkipped] = useState(false);

  const { isSubmitting, isValid, setFieldValue, values } =
    useFormikContext<OnboardingFormValues>();

  const setFormPageInfo = useSetFormPageInfo<OnboardingFormValues>();
  useEffect(() => {
    setFormPageInfo({
      submitHandler: async ({ values: submitValues, setIsSubmitting }) => {
        setIsSubmitting(true);
        let portalOnboarding: AxiosResponse<PortalConfig> | null = null;
        try {
          const {
            foundByCustom,
            challengeResponse: _challengeResponse,
            ...portalSetupInputValues
          } = submitValues;
          if (portalSetupInputValues.foundBy === FOUND_PORTAL_OTHER_VALUE) {
            portalSetupInputValues.foundBy = foundByCustom;
          }
          portalOnboarding = await OnboardingClient.SavePortalOnboarding(
            portalConfig,
            portalSetupInputValues,
          );
        } catch (err) {
          setIsSubmitting(false);
          const e = ensureApiError(err);
          dispatch(
            alertSnackbar({
              errorMessage: e.message,
            }),
          );
          return;
        }

        // after onboarding is complete, track the sign-up
        // event for third party services.
        TrackPartnerStackSignUp({
          email: submitValues.email,
          name: submitValues.name,
          customer_key: portalOnboarding.data.fields.portalHeader,
          provider_key: portalOnboarding.data.customerId,
        });

        // after onboarding is saved, we should be able to login to the portal
        // and redirect the user to the new page
        let user: CognitoUserWithAttributes | null = null;

        // on get started with another portal screen
        // when user selects an existing authenticated email
        // he will skip the password step, then sign-in with email
        // and password won't work, so we need to sign-in with email only
        // using Auth.currentAuthenticatedUser
        if (!values.password) {
          user = await Auth.currentAuthenticatedUser();

          // if we have a logged in user and the we were trying to onboard a user
          // with google as auth provider, we need to now refresh the token
          // This is required so that the token can contain the updated permissions
          // from the created portal / database user via the onboarding api.
          // To acheive this we redirect to the login page and set the query params
          // to confirm the login
          if (
            user &&
            values.loginSource === CognitoHostedUIIdentityProvider.Google
          ) {
            // we use the customState to hold the redirect uris, so to redirect the user to the
            // created portal we set some query params
            // - action => this triggers the auto login in the login page
            // - next => after login is done, this redirects the user to the portal they were trying to login
            // - email => provided for error handling
            const next = `login?action=confirm-login&next=/portal/${portalOnboarding.data.id}&email=${user.attributes.email}`;
            await Auth.federatedSignIn({
              provider: CognitoHostedUIIdentityProvider.Google,
              customState: next,
            });

            // the login process will now be completed via the login page, so we can return from here.
            trackInfoSubmitted(values);
            return;
          }
        } else {
          // Try to get current authenticated user first (handles MFA case where user is already authenticated)
          try {
            user = await Auth.currentAuthenticatedUser();
          } catch (err) {
            // If not authenticated, sign in with password
            const requestInfo = await getAuthRequestInfo();
            user = await Auth.signIn(
              values.email,
              values.password,
              GetSignInMetadata(undefined, requestInfo),
            );
          }
        }

        // set the app user cookie so that it can be used to authenticate the user
        await UsersClient.setAppUserCookie(
          user as CognitoUser,
          portalOnboarding.data.fields.portalHeader,
          values.portalUrl,
          values.email,
        );

        trackInfoSubmitted(values);

        window.location.replace(
          `/portal/${portalOnboarding.data.fields.portalHeader}`,
        );
      },
      validationSchema: skipped ? undefined : validationSchema,
    });
  }, [setFormPageInfo, skipped]);

  const priorityLabels = getPriorityLabels(t);

  return (
    <>
      <OnboardingContentLayout
        title="Start customizing your workspace"
        description="You can update these answers later at any time."
      >
        <div>
          <OnboardingQuestionLabel>
            What are your top priorities right now?{' '}
            <span className="text-gray-small">(Select all that apply)</span>
          </OnboardingQuestionLabel>
          <ToggleChipGroup
            options={priorityOptions}
            value={values.priorities}
            onOptionClick={(value) => {
              let newPriorities: string[];
              if (values.priorities.includes(value)) {
                newPriorities = filter(values.priorities, (v) => v !== value);
              } else {
                newPriorities = [...values.priorities, value];
              }
              setFieldValue('priorities', newPriorities);
              setFieldValue(
                'prioritiesLabels',
                newPriorities
                  .map(
                    (priority) =>
                      priorityLabels[priority as keyof typeof priorityLabels],
                  )
                  .join(', '),
              );
            }}
            numColumns={2}
          />
        </div>

        <div>
          <OnboardingQuestionLabel>
            What features do you want to start with in your workspace?{' '}
            <span className="text-gray-small">(Select all that apply)</span>
          </OnboardingQuestionLabel>
          <ToggleChipGroup
            options={featureOptions}
            value={values.enabledFeatures}
            onOptionClick={(value) => {
              if (values.enabledFeatures.includes(value)) {
                setFieldValue(
                  'enabledFeatures',
                  filter(values.enabledFeatures, (v) => v !== value),
                );
              } else {
                setFieldValue('enabledFeatures', [
                  ...values.enabledFeatures,
                  value,
                ]);
              }
            }}
          />
        </div>
      </OnboardingContentLayout>

      <div className="w-full sm:max-w-[360px] text-lg ml-auto mr-auto mt-10">
        <Button
          className="w-full h-12"
          label="Continue"
          type="submit"
          disabled={!isValid || isSubmitting}
          loading={isSubmitting}
        />
        <Button
          className="w-full h-12 mt-2"
          label="Skip"
          type="submit"
          variant="text"
          onClick={() => setSkipped(true)}
          disabled={isSubmitting}
          loading={isSubmitting}
        />
      </div>
    </>
  );
};
